/**
 * Export: markdown out, byte-exact (ytext.toString() IS the file).
 * Single note -> .md download; folder or vault -> zip preserving structure.
 */
import { zipSync, strToU8 } from "fflate";
import type { WorkspaceItem } from "@hydra-notes/shared";
import { getData, resolveItem } from "./store";

function sanitizeName(title: string): string {
  const base = (title || "Untitled").replace(/[<>:"/\\|?* -]/g, "_").trim() || "Untitled";
  return base.slice(0, 150);
}

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export async function exportNote(noteId: string): Promise<void> {
  const res = resolveItem(noteId);
  if (!res) return;
  const md = await res.ctx.notes.readMarkdown(noteId);
  download(new Blob([md], { type: "text/markdown" }), `${sanitizeName(res.item.title)}.md`);
}

/** Export a folder subtree (or the whole personal vault when rootId is null) as a zip. */
export async function exportZip(rootId: string | null): Promise<number> {
  // a rootId in a shared folder exports from that space; null = personal vault
  const data = rootId ? (resolveItem(rootId)?.ctx ?? getData()) : getData();
  const items = data.workspace.getSnapshot().filter((i) => !i.trashedAt);

  const inScope = (item: WorkspaceItem): boolean => {
    if (rootId === null) return true;
    let cur: WorkspaceItem | undefined = item;
    let hops = 0;
    while (cur && hops++ < 100) {
      if (cur.id === rootId) return true;
      cur = cur.parentId ? items.find((i) => i.id === cur!.parentId) : undefined;
    }
    return false;
  };

  const pathOf = (item: WorkspaceItem): string[] => {
    const parts: string[] = [];
    let cur = item.parentId ? items.find((i) => i.id === item.parentId) : undefined;
    let hops = 0;
    while (cur && hops++ < 100) {
      if (rootId !== null && cur.id === rootId) break;
      parts.unshift(sanitizeName(cur.title));
      cur = cur.parentId ? items.find((i) => i.id === cur!.parentId) : undefined;
    }
    return parts;
  };

  const files: Record<string, Uint8Array> = {};
  const used = new Set<string>();
  let count = 0;
  for (const item of items) {
    if ((item.type !== "note" && item.type !== "card") || !inScope(item)) continue;
    const md = await data.notes.readMarkdown(item.id);
    let rel = [...pathOf(item), `${sanitizeName(item.title)}.md`].join("/");
    let n = 2;
    while (used.has(rel.toLowerCase())) {
      rel = rel.replace(/\.md$/, ` (${n++}).md`);
    }
    used.add(rel.toLowerCase());
    files[rel] = strToU8(md);
    count++;
  }

  const rootItem = rootId ? data.workspace.getItem(rootId) : null;
  const zipName = rootItem ? `${sanitizeName(rootItem.title)}.zip` : "hydra-notes-export.zip";
  const zipped = zipSync(files, { level: 6 });
  download(new Blob([zipped.slice().buffer], { type: "application/zip" }), zipName);
  return count;
}
